import type { OwnerTerritoriesState } from "./useOwnerTerritories";
import type { OwnerTerritory } from "../../domain/owner-territories";
import type { TerritoryPreview } from "./useTerritoryPreviews";
import { TerritoryCard } from "./TerritoryCard";
import { useI18n } from "../../i18n";
import { cn } from "../cn";

/**
 * MyTerritories — deterministic list of the authenticated owner's real
 * continuous territories. Renders distinct loading / empty / unavailable /
 * resolved states. No sample/mock cards; no partial values when unavailable.
 */
export function MyTerritories({
  state,
  previews,
  selectedKey,
  unreadIdentityIds,
  onOpen,
}: {
  state: OwnerTerritoriesState;
  previews: Map<string, TerritoryPreview>;
  selectedKey: string | null;
  unreadIdentityIds: Set<string>;
  onOpen: (key: string) => void;
}) {
  const { t } = useI18n();

  if (state.status === "loading") {
    return (
      <div
        data-testid="dashboard-loading"
        className="rounded-lg border border-border-soft bg-panel p-8 text-[13px] text-text-muted font-mono"
      >
        {t("dashboard.list.resolving")}
      </div>
    );
  }

  if (state.status === "unavailable") {
    return (
      <div
        data-testid="dashboard-unavailable"
        className="rounded-lg border border-border-soft bg-panel p-8"
      >
        <p className="m-0 text-[14px] text-text-primary font-display">
          {t("dashboard.list.unavailableTitle")}
        </p>
        <p className="mt-2 text-[12px] text-text-muted">
          {t("dashboard.list.unavailableBody")}
        </p>
      </div>
    );
  }

  if (state.status === "empty") {
    return (
      <div
        data-testid="dashboard-empty"
        className="rounded-lg border border-border-soft bg-panel p-8 text-center"
      >
        <p className="m-0 text-[15px] text-text-primary font-display">
          {t("dashboard.list.emptyTitle")}
        </p>
        <p className="mt-2 text-[12px] text-text-muted">
          {t("dashboard.list.emptyBody")}
        </p>
        <a
          data-testid="dashboard-empty-explore-link"
          href="#purchase"
          className={cn(
            "facet-btn mt-5 h-7 px-3 no-underline",
            "text-[10px] font-semibold tracking-[0.16em]",
          )}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
            <rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M2 6.5h12M6.5 2v12" stroke="currentColor" strokeWidth="1" opacity="0.6" />
          </svg>
          {t("dashboard.list.exploreGrid")}
        </a>
      </div>
    );
  }

  const territories: OwnerTerritory[] =
    state.status === "resolved" ? state.territories : [];

  return (
    <div data-testid="my-territories-list" className="space-y-4">
      {territories.map((terr, i) => {
        const identityId = previews.get(terr.key)?.identityId ?? null;
        const unread = identityId ? unreadIdentityIds.has(identityId) : false;
        return (
          <TerritoryCard
            key={terr.key}
            ordinal={i + 1}
            territory={terr}
            preview={previews.get(terr.key)}
            selected={selectedKey === terr.key}
            unread={unread}
            onOpen={onOpen}
          />
        );
      })}
    </div>
  );
}
